import * as I from 'immutable';
import React from 'react';

import * as Remote from 'app/modules/Remote';
import {ApiFeatureData} from 'app/modules/Remote/Feature';
import FEATURE_DATA_JSON from 'app/tests/__fixtures__/featureData_property-monitor.json';
import {CachedApiGetter, CachedApiGetterOptions, useCachedApiGet} from 'app/utils/hookUtils';

export interface FeatureDataActions {
  /** numeric arg is the feature ID */
  getFeatureData: CachedApiGetter<[number], I.ImmutableOf<ApiFeatureData[]>>;
  invalidateFeatureData: (featureId: number) => void;
}

export const FeatureDataContext = React.createContext<FeatureDataActions | null>(null);

export interface FeatureDataProviderProps {
  featureCollectionId: number;
  children: React.ReactNode;
}

/**
 * Provider component that makes featureData fetching available to child components
 * via React Context, eliminating the need for prop drilling.
 *
 * The provider is scoped to a specific feature collection ID, matching the
 * dependency pattern used by useCachedApiGet.
 */
export const FeatureDataProvider: React.FunctionComponent<FeatureDataProviderProps> = ({
  featureCollectionId,
  children,
}) => {
  const [getFeatureData, {invalidate: invalidateFeatureData}] = useCachedApiGet(
    async function* ([fcId], fId: number) {
      // we use dataGen here so that this load will be interrupted if we stop
      // needing it.
      return yield* Remote.api.featureCollections
        .features(fcId)
        .dataGen(fId, {perPage: 100}, {getAllPages: 6});
    },
    [featureCollectionId]
  );

  const value = React.useMemo<FeatureDataActions>(
    () => ({
      getFeatureData,
      invalidateFeatureData: (featureId: number) => invalidateFeatureData(featureId),
    }),
    [getFeatureData, invalidateFeatureData]
  );

  return <FeatureDataContext.Provider value={value}>{children}</FeatureDataContext.Provider>;
};

/**
 * The same getter for a share-link page, which has no signed-in session: imagery comes from
 * the link's own data route, with unshareable sources already withheld.
 */
export const PublicFeatureDataProvider: React.FunctionComponent<{
  uuid: string;
  children: React.ReactNode;
}> = ({uuid, children}) => {
  const [getFeatureData, {invalidate: invalidateFeatureData}] = useCachedApiGet(
    async ([linkUuid], featureId: number) =>
      (await Remote.api.public.featureData(linkUuid, featureId)).get('data') as I.ImmutableOf<
        ApiFeatureData[]
      >,
    [uuid]
  );

  const value = React.useMemo<FeatureDataActions>(
    () => ({
      getFeatureData,
      invalidateFeatureData: (featureId: number) => invalidateFeatureData(featureId),
    }),
    [getFeatureData, invalidateFeatureData]
  );

  return <FeatureDataContext.Provider value={value}>{children}</FeatureDataContext.Provider>;
};

/**
 * Hook to access the featureData getter and invalidation functions from context.
 *
 * Use this hook when you need:
 * - To fetch data for multiple features (e.g., in loops or conditionals)
 * - Direct access to the invalidation function
 * - More control over when/how the getter is called
 *
 * Example: Fetching data for multiple features in a loop
 * ```tsx
 * const {getFeatureData} = useFeatureData();
 * for (const feature of features) {
 *   const data = getFeatureData(feature.id);
 *   // process data...
 * }
 * ```
 *
 * For simple single-feature cases, prefer `useFeatureDataForFeature()` instead.
 *
 * @throws Error if used outside of FeatureDataProvider
 */
export function useFeatureData(): FeatureDataActions {
  const context = React.useContext(FeatureDataContext);
  if (context === null) {
    throw new Error('useFeatureData must be used within a FeatureDataProvider');
  }
  return context;
}

/**
 * Convenience hook to get feature data for a specific feature.
 *
 * This hook wraps useFeatureData and provides a simpler API for components
 * that just need the data for a single feature. It automatically handles
 * null/undefined featureId cases and extracts the value from StatusMaybe,
 * returning just the feature data list or null.
 *
 * **When to use this hook:**
 * - You need data for a single feature (most common case)
 * - The featureId might be null/undefined (e.g., during loading or when no feature is selected)
 * - You want a simple `featureData | null` return type without StatusMaybe boilerplate
 *
 * **When NOT to use this hook:**
 * - You need to fetch data for multiple features (use `useFeatureData()` instead)
 * - You need access to `invalidateFeatureData` (use `useFeatureData()` instead)
 * - You need to distinguish between loading/error/loaded states (use `useFeatureData()` + `getFeatureData()` instead)
 * - You're calling the getter conditionally or in loops (use `useFeatureData()` instead)
 *
 * Example: Getting data for the currently selected feature
 * ```tsx
 * const featureData = useFeatureDataForFeature(selectedFeatureId, {
 *   afterMs: Date.now() - 1000 * 60 * 60, // within last hour
 *   allowStaleMs: true,
 * });
 * // featureData is I.ImmutableOf<ApiFeatureData[]> | null - no StatusMaybe conversion needed!
 * ```
 *
 * Benefits over using `getFeatureData` directly:
 * - Automatic null/undefined handling (returns null instead of calling with invalid ID)
 * - Automatic StatusMaybe extraction (returns the value directly, or null if not loaded/error)
 * - Cleaner API for single-feature use cases
 * - Less boilerplate code (no need to check `status === 'some'`)
 *
 * @param featureId - The ID of the feature to get data for (can be null/undefined)
 * @param options - Optional cache staleness options
 * @returns The feature data list, or null if featureId is null/undefined, data is loading, or there's an error
 *
 * @throws Error if used outside of FeatureDataProvider
 */
export function useFeatureDataForFeature(
  featureId: number | null | undefined,
  options?: CachedApiGetterOptions
): I.ImmutableOf<ApiFeatureData[]> | null {
  const {getFeatureData} = useFeatureData();

  if (featureId === null || featureId === undefined) {
    return null;
  }

  const result = getFeatureData(featureId, options);
  return result?.status === 'some' ? result.value : null;
}

/**
 * Fake provider for testing that provides mock implementations of featureData actions.
 */
export const FakeFeatureDataProvider: React.FunctionComponent<React.PropsWithChildren> = ({
  children,
}) => {
  const FAKE_FEATURE_DATA = I.fromJS(FEATURE_DATA_JSON);

  const value = React.useMemo<FeatureDataActions>(
    () => ({
      getFeatureData: () => ({status: 'some', value: FAKE_FEATURE_DATA}),
      invalidateFeatureData: () => {},
    }),
    [FAKE_FEATURE_DATA]
  );

  return <FeatureDataContext.Provider value={value}>{children}</FeatureDataContext.Provider>;
};
